const {
  grantByOrgId,
  getPushXConnection,
  cleanup,
  setupGrantByAccountIdPushX
} = usePushXService();
const { gigyaLoadedPromise, getAllUserData } = useGigyaManagerService();
const {
  navigateUserBasedOnRole,
  navigateToDashboardApp,
  navigateToLearningPathApp,
  navigateToClassApp,
  navigateToFocApp
} = useInterAppNavigationService();
const { logInfoToBackend } = useCustomBackendLogger();
const {
  setDataFromUserAuthenticatedCall,
  clearUserdata: clearUserdataFromService,
  checkUserAuthenticatedCache
} = useOnboardingAuth();
const { getC1UserRole } = useAppUtils();
const { getCookie } = useHttpHelper();

export function useLtiApiService() {
  let gigyaUserdata: any = false;
  let displayPopupBlockedMessage = shallowRef(false);
  let userEntitlements: any = false;
  let captureUserEnrollmentAdd = true;
  let isShadowProvisionComplete = false;
  let isEnrollmentEventReceived = false;
  let shadowProvisionSuccessComplete = false;
  const scoreDetailFormatTypes:any = {
    mock: "MOCK",
    none: "none"
  }
  const productModelTypes:any = {
    test: 'TEST'
  }
  let schoolEntitlements: any = false;

  const getUserSpaces = async () => await $fetch('/apigateway/spaces');

  const getUserEntitlements = async (spaceId: string) =>
    await $fetch(`/apigateway/entitlements?spaceId=${spaceId}`);

  const fetchProductsDetails = async (productcodes: any) => 
    await $fetch('/apigateway/productdetails', {
      method: 'POST',
      body: productcodes
    });
  
  const getOverlayJson = async (data: any) =>
    await $fetch('/apigateway/lti/overlayjson', {
      method: 'POST',
      body: data
    });

  const getBundleName = async (bundleCode: any) =>
    await $fetch('/apigateway/bundleName/' + bundleCode);

  const getParticularClassData = async (spaceId: string, classId: string) =>
    await $fetch('/apigateway/' + spaceId + '/class/' + classId);

  const getParticularClassAndSpaceData = async (
    spaceId: string,
    classId: string
  ) => await $fetch('/apigateway/' + spaceId + '/classAndSpace/' + classId);

  const associateClassProducts = async (data: any) =>
    await $fetch('/apigateway/associate/product', {
      method: 'POST',
      body: data
    });

  const shadowProvision = async (data: any) =>
    await $fetch('/apigateway/shadowProvision', {
      method: 'POST',
      body: data
    });

  const shadowProvisionOnlySpace = async (data: any) =>
    await $fetch('/apigateway/shadowProvisionOnlySpace', {
      method: 'POST',
      body: data
    });

  const getParticularShadowClass = async (
    spaceId: string,
    extClassId: string,
    data: any
  ) =>
    await $fetch('/apigateway/' + spaceId + '/shadowClass/' + extClassId, {
      method: 'POST',
      body: data
    });

  const checkParticularShadowClass = async (extClassId: string, data: any) =>
    await $fetch('/apigateway/checkShadowClass/' + extClassId, {
      method: 'POST',
      body: data
    });
  const createGradebookItem = async (gradebookItemData: any) =>
    await $fetch('/apigateway/lti/createGradebookItem', {
      method: 'POST',
      body: {
        gradebookItemData
      }
    });
  const requestDeepLinkingForm = async (ltik: string, resourceData: any) =>
    await $fetch('/apigateway/lti/deeplink', {
      method: 'POST',
      body: { ltik, resourceData }
    });

  /**
   * check user existence and login user
   */
  const ltiLogin = async (gigyaUserData: any, ltiUserInfo: any, ltik: any, jwt: any) => {
    const headersObj = {
      headers: {
        'csrf-token': getCookie('csrf-token')
      }
    };
    return await $fetch('/apigateway/lti/login', {
      method: 'POST',
      body: { gigyaUserData, ltiUserInfo, ltik, jwtToVerify: jwt },
      headers: headersObj.headers
    });
  };

  const studentProductActivation = async (
    activationCode: string,
    gigyaData: any,
    isClassLicenceActivated: boolean,
    spaceId: string,
    classId: string
  ) => {
      return await $fetch('/apigateway/student/product/activationcode', {
        method: 'POST',
        body: {
          activationCode,
          gigyaData,
          ...(isClassLicenceActivated && { type: 'class license', spaceId, classId })
        }
      });
  }

  const getMarkingQueueCount = async (params: any) => {
    const markingQueueData = await $fetch('/apigateway/lti/markingqueuecount', {
      method: 'POST',
      body: params
    });
    return markingQueueData;
  };

  const setLTIKData = (queryParams: any, ltikDataParam: any) => {
    const ltikData = useState('ltikData');
    const previousValue: any = ltikData.value;
    ltikData.value = {
      ...previousValue,
      ...ltikDataParam,
      ...{
        ltik: queryParams.ltik,
        resource: queryParams.resource,
        productCode: decodeURIComponent(queryParams.resource).split('/')[2]
      }
    };
  };

  const getLTIKData = () => {
    return useState('ltikData').value;
  };

  const getGigiyaUserdata = async () => {
    if (!gigyaUserdata) {
      !window.gigya && (await gigyaLoadedPromise());
      gigyaUserdata = await getAllUserData();
    }
    return gigyaUserdata;
  };

  //error handling for selecting a different product
  const checkDifferentProductLink = async (
    classDetails: any,
    bundleCode: string
  ) => {
    if (!classDetails.bundles?.[bundleCode]) {
      const bundleName: any = await getBundleName(bundleCode);
      navigateTo({
        path: '/lti-onboarding-error/differentProductLink',
        query: {
          title: bundleName
        }
      });
      return true;
    }
  };

  /**
   * Navigate based on resource
   */
  const navigateLTItoApp = async (
    spaceKey: string,
    classid: string,
    product: any,
    role: string,
    ltik: string,
    resource: string,
    openInNewTab = false
  ) => {
    const ltikData: any = getLTIKData();
    const resourceParts = decodeURIComponent(resource).split('/');
    const options: any = { queryParams: { ltik: ltik } };
    const bundleCode = resourceParts[1];
    const classDetails: any = await getClassDetails(spaceKey, classid);
    let isProductActivatedByTeacher, productFound, isProductExpired;
    if ((role === 'teacher' || role ==='admin') && bundleCode) {
      const isProductDifferent = await checkDifferentProductLink(
        classDetails,
        bundleCode
      );
      if (isProductDifferent) return;
      const products = classDetails.bundles[bundleCode].products;
      productFound = products.find(
        (item: any) => item['dls-product'].code == resourceParts[2]
      );
      const isTeacherGatedComponent =
        productFound?.activation?.gated &&
        Object.hasOwn(productFound.activation.gated, 'teacher');
      if (isTeacherGatedComponent) {
        const userEntitlementInfo = await isProductEntitledByUser(ltikData.productCode, spaceKey);
        isProductActivatedByTeacher = userEntitlementInfo.isProductEntitled;
        isProductExpired = userEntitlementInfo.isExpired;
      }
    }

    if (
      resourceParts[0] == 'component' &&
      ['student', 'teacher', 'admin'].indexOf(role) > -1
    ) {
      const isProductDifferent = await checkDifferentProductLink(
        classDetails,
        bundleCode
      );
      if (isProductDifferent) return;
      /* check component type and redirect user to activity */
      const products = classDetails.bundles[bundleCode].products;
      const componentType = products.find(
        (item: any) => item['dls-product'].code == resourceParts[2]
      )['ext-product'].type;
      // In case component launched is EXT_RES
      if (['EXT_RES', 'TG', 'ASSET', 'PPLUS'].indexOf(componentType) !== -1) {
        //if product is not activated or is expired
        if (isProductActivatedByTeacher === false || isProductExpired) {
          sendToActivateMaterial({
            productFound,
            classid,
            bundleCode,
            ltik,
            spaceKey,
            isProductExpired
          });
          return;
        }
      }
      if (componentType === 'imgbook') {
        const isPPlusComp = extractIsPPlusComp(ltikData.targetLinkUri);

        // Is PPlus ebook(deeplink), then show error page for student
        if(isPPlusComp && role === 'student') {
          return navigateTo({
            path: '/lti-onboarding-error/teacherResourceLink',
            query: { title: ltikData.resourceTitle }
          });
        } else {
          const ltiGradebookItem = await createGradebookItem({
            platformID: ltikData.platformID,
            oid: ltikData.oid,
            contextId: ltikData.contextId,
            ltiPlatformUserId: ltikData.ltiPlatformUserId,
            resourceId: ltikData.resourceId,
            messageType: ltikData.messageType,
            targetLinkUri: ltikData.targetLinkUri,
            sapCdcUserId: ltikData.sapCdcUserId,
            lineItem: ltikData.lineItem,
            role: ltikData.role
          });
          options.queryParams.ltiGiId = ltiGradebookItem.ltiGiId;
          return navigateToFocApp(
            [
              spaceKey,
              'class',
              classid,
              'product',
              bundleCode,
              'studentbook',
              resourceParts[2],
              'view'
            ],
            options
          );
        }
      } else if (componentType === 'OLP') {
        // resource bank
        return navigateToDashboardApp([
          (role === 'teacher' || role === 'admin') ? 'teacher' : 'learner',
          spaceKey,
          'resourcebank',
          resourceParts[2],
          classid
        ],
        options
      );
      } else if (componentType === 'EXT_RES') {
        return navigateToDashboardApp(
          [
            (role === 'teacher' || role === 'admin') ? 'teacher' : 'learner',
            spaceKey,
            'externalresources',
            resourceParts[2],
            classid
          ],
          options
        );
      } else if (componentType === 'TG' || componentType === 'ASSET' || componentType === 'PPLUS') {
        return navigateToDashboardApp(
          [
            'teacher',
            spaceKey,
            'bundle',
            resourceParts[1],
            'class',
            classid,
            'digital-component',
            resourceParts[2]
          ],
          options
        );
      }
    }

    if (role === 'student') {
      if (resourceParts[0] == 'unit') {
        options.queryParams.ltiGiId = ltikData.ltiGradebookItemId;
        navigateToLearningPathApp(
          [
            'learner',
            spaceKey,
            'product',
            resourceParts[2],
            'class',
            classid,
            'unit',
            resourceParts[3]
          ],
          options
        );
      } else if (resourceParts[0] == 'activity') {
        options.queryParams.ltiGiId = ltikData.ltiGradebookItemId;
        // NEMO-18658 Launch tests in new window in an iframe
        if(window.frameElement || window.self !== window.top) {
          displayPopupBlockedMessage.value = true;
          options.target = "_blank";
        }
        navigateToLearningPathApp(
          [
            'learner',
            spaceKey,
            'product',
            resourceParts[2],
            'class',
            classid,
            'unit',
            resourceParts[3],
            'activity',
            resourceParts[4]
              ? encodeURIComponent(resourceParts.slice(3).join('/'))
              : resourceParts[3]
          ],
          options
        );
      } else if (resourceParts[0] == 'marking') {
        navigateTo({
          path: '/lti-onboarding-error/teacherResourceLink',
          query: { title: ltikData.resourceTitle }
        });
      } else if (resourceParts[0] == 'assignment') {
        options.queryParams.ltiGiId = ltikData.ltiGradebookItemId;
        navigateToLearningPathApp(
          [
            'learner',
            spaceKey,
            'class',
            classid,
            'product',
            resourceParts[2],
            'assignment',
            resourceParts[3]
          ],
          options
        );
      } else {
        navigateToDashboardApp(['learner', 'dashboard']);
      }
    } else if (role === 'teacher' || role === 'admin') {
      if (resourceParts[0] == 'unit' || resourceParts[0] == 'activity' || resourceParts[0] == 'assignment') {
        //if product is not activated or is expired
        if (isProductActivatedByTeacher === false || isProductExpired) {
          const unit = resourceParts[3];
          const activity = resourceParts[4]
            ? encodeURIComponent(resourceParts.slice(3).join('/'))
            : resourceParts[3];
          const assignment = resourceParts[3];
          sendToActivateMaterial({
            productFound,
            classid,
            bundleCode,
            ltik,
            spaceKey,
            unit,
            ...(resourceParts[0] == 'activity' && { activity: activity }),
            ...(resourceParts[0] == 'assignment' && { assignment: assignment }),
            isProductExpired
          });
          return;
        }
      }
      if (resourceParts[0] == 'deeplink') {
        if(openInNewTab) {
          options.target = "_blank";
        }
        navigateToLearningPathApp(
          [
            'teacher',
            spaceKey,
            'class',
            classid,
            'ltideeplinking',
            'product',
            product.productCode
          ],
          options
        );
      } else if (resourceParts[0] == 'unit') {
        navigateToLearningPathApp(
          [
            'teacher',
            spaceKey,
            'class',
            classid,
            'product',
            resourceParts[2],
            'unit',
            resourceParts[3]
          ],
          options
        );
      } else if (resourceParts[0] == 'activity') {
        // NEMO-18658 Launch tests in new window in an iframe
        if (window.frameElement || window.self !== window.top) {
          displayPopupBlockedMessage.value = true;
          options.target = "_blank";
        }
        navigateToLearningPathApp(
          [
            'teacher',
            spaceKey,
            'class',
            classid,
            'product',
            resourceParts[2],
            'unit',
            resourceParts[3],
            'activity',
            resourceParts[4]
              ? encodeURIComponent(resourceParts.slice(3).join('/'))
              : resourceParts[3]
          ],
          options
        );
      } else if (resourceParts[0] == 'assignment') {
        options.queryParams.ltiGiId = ltikData.ltiGradebookItemId;
        navigateToLearningPathApp(
          [
            'teacher',
            spaceKey,
            'class',
            classid,
            'product',
            resourceParts[2],
            'assignment',
            resourceParts[3]
          ],
          options
        );
      } else if (resourceParts[0] == 'class') {
        navigateToClassApp(
          ['teacher', spaceKey, 'class', classid, 'view'],
          options
        );
      } else if (resourceParts[0] == 'marking') {
        navigateToClassApp(
          ['teacher', spaceKey, 'class', classid, 'marking'],
          options
        );
      } else {
        navigateToDashboardApp(['teacher', 'dashboard']);
      }
    } else {
      navigateUserBasedOnRole(role);
    }
  };

  const extractIsPPlusComp = (url?: string) => {
    if (!url) return false;
    return url?.includes('isPPlusComp=true');
  }

  const getClassDetails = async (spaceKey: string, classId: string) => {
    if (!useState('classData').value) {
      useState('classData').value = await getParticularClassData(
        spaceKey,
        classId
      );
    }
    return useState('classData').value;
  };

  const getSpaceAndClassDetails = async (spaceKey: string, classId: string) => {
    const classData = useState('classData').value;
    const spaceData = useState('spaceData').value;
    const spaceAndClassData: any = {};
    if (classData && !spaceData) {
      spaceAndClassData.classData = classData;
      const spacesReturnData: any = await getUserSpaces();
      spaceAndClassData.spaceData = spacesReturnData.spaces;
    } else if (!classData && spaceData) {
      spaceAndClassData.classData = await getParticularClassData(
        spaceKey,
        classId
      );
      spaceAndClassData.spaceData = spaceData;
    } else if (!classData && !spaceData) {
      const classAndSpacesDataResponse: any =
        await getParticularClassAndSpaceData(spaceKey, classId);
      spaceAndClassData.classData = classAndSpacesDataResponse.classData;
      spaceAndClassData.spaceData = classAndSpacesDataResponse.spaceData;
    } else {
      spaceAndClassData.classData = classData;
      spaceAndClassData.spaceData = spaceData;
    }
    useState('classData').value = spaceAndClassData.classData;
    useState('spaceData').value = spaceAndClassData.spaceData;
    return spaceAndClassData;
  };

  const isMQASchool = (userSpaceData: any, spaceId = undefined) => {
    let userSpace = userSpaceData;
    if (userSpaceData.entities) {
      userSpace = userSpaceData.entities.find(
        (space: any) => space.space_key === spaceId
      );
    }
    return userSpace?.dls_org_id && userSpace.dls_org_id.indexOf('mqa') === 0;
  };

  const isProductEntitledByUser = async (productCode: string, spaceId: string) => {
    if (!userEntitlements || !schoolEntitlements) {
      const entitlementResponse = await getUserEntitlements(spaceId);
      userEntitlements = entitlementResponse?.entitlementsForAUser;
      schoolEntitlements = entitlementResponse?.schoolLevelEntitlementForUser;
    }

    const schoolEntitledProductIndex: any = schoolEntitlements?.entities.findIndex((entity: any) => entity.productcode == productCode)

    const entitledProductIndex: any = userEntitlements.entities.findIndex((entity: any) => entity.productcode == productCode);
    let isExpired = false, endDate;
    let isProductEntitled: boolean = false;
    let isSchoolEntitlementExpired: boolean = true;
    let isSchoolEntitlementStarted: boolean = false;
    
    if (schoolEntitlements && schoolEntitledProductIndex > -1 && schoolEntitlements?.entities[schoolEntitledProductIndex]) {
      isSchoolEntitlementStarted = Date.now() >= schoolEntitlements.entities[schoolEntitledProductIndex].startdate;
      if(isSchoolEntitlementStarted) {
        endDate = schoolEntitlements.entities[schoolEntitledProductIndex].enddate;
        isProductEntitled = true;
        isExpired = Date.now() > endDate;
        isSchoolEntitlementExpired = isExpired;
      }
    } 
    if ((!isSchoolEntitlementStarted || isSchoolEntitlementExpired) && userEntitlements && entitledProductIndex > -1 && userEntitlements?.entities && userEntitlements.entities[entitledProductIndex] && userEntitlements.entities[entitledProductIndex].entitlement) {
      endDate = userEntitlements.entities[entitledProductIndex].entitlement.end_date;
      isExpired = Date.now() > endDate;
      isProductEntitled = true;
    }  
    return { isProductEntitled, isExpired, endDate };
  };

  const getAllBundleDetails = async (spaceKey: string) => {
    if (!useState('bundlesData').value) {
      if (!useState('bundlesDataPromise').value) {
        const ltikData: any = getLTIKData();
        useState('bundlesDataPromise').value = $fetch('/apigateway/' + spaceKey + '/getAllBundles', {
          method: 'POST',
          body: { umbrellaProductsList: ltikData.umbrellaProductsList }
        });
      }
      useState('bundlesData').value = await useState('bundlesDataPromise').value;
    }
    return useState('bundlesData').value;
  };

  const getfallbackLosCounts = async () => {
    if (!useState('fallbackLosCountData').value) {
      if (!useState('fallbackLosCountDataPromise').value) {
        useState('fallbackLosCountDataPromise').value = $fetch('/apigateway/fallbackLosCounts');
      }
      useState('fallbackLosCountData').value = await useState('fallbackLosCountDataPromise').value;
    }
    return useState('fallbackLosCountData').value;
  };

  const addProductsToClass = async (
    ltiSpaceKey: string,
    ltiClassId: string,
    selectedBundles: any,
    ltik: string
  ) => {
    const ltikData: any = getLTIKData();
    const spacesDataFromApi: any = await getUserSpaces();
    useState('spaceData').value = spacesDataFromApi.spaces;
    const userSpaceData = spacesDataFromApi.spaces.entities.find(
      (space: any) => space.space_key == ltiSpaceKey
    );
    let productsCode: any = [];
    selectedBundles.forEach((selectedBundle: any) => {
      const productsCodeFromBundle = selectedBundle.products
        .reduce((results: any, product: any) => {
          product['ext-product'].type === 'OLP' &&
            product.classes &&
            product.classes.enabled &&
            results.push(product['dls-product'].code);
          return results;
        }, [])
        .slice(0, 10);
      productsCode = productsCode.concat(productsCodeFromBundle);
    });
    const productsWithPushEventsReceived = [];
    grantByOrgId(userSpaceData.dls_org_id, ltiSpaceKey).then(async() => {
      const pushXConnection: any = await getPushXConnection(userSpaceData.dls_org_id);
      const channelObj = {
        userid: userSpaceData.dls_teacher_id,
        orgid: userSpaceData.dls_org_id,
        channel: 'systemevents.CLASS.PRODUCT_ASSOCIATION_ADD'
      };

      pushXConnection.on(channelObj, async (eventContext: any) => {
        logInfoToBackend({
          userId: userSpaceData.dls_teacher_id,
          orgId: userSpaceData.dls_org_id,
          class: ltiClassId,
          productsCode: productsCode,
          context: 'TYPE=SYSTEM_EVENTS, Associate Product With Class LTI Flow',
          event: eventContext
        }).then();

        if (
          eventContext.events?.body &&
          eventContext.events.body.classid === ltiClassId
        ) {
          if (eventContext.events.body.status === 'success') {
            productsWithPushEventsReceived.push(
              eventContext.events.body.productcode
            );
            if (productsWithPushEventsReceived.length == productsCode.length) {
              // redirect based on resource type
              useState('classData').value = false;
              if (ltikData.resource === 'deeplink') {
                navigateTo({
                  path: `/lti-teacher-dashboard/${ltiSpaceKey}/class/${ltiClassId}/ltik/${ltik}`
                });
              } else {
                await navigateLTItoApp(
                  ltiSpaceKey,
                  ltiClassId,
                  { productCode: ltikData.productCode },
                  ltikData.role,
                  ltik,
                  ltikData.resource
                );
              }
            }
          } else if (eventContext.events.body.status === 'error') {
            throw new Error(JSON.stringify(eventContext));
          }
        }
      });

      pushXConnection.on(
        { channel: 'pushx_status' },
        (subscriptionStatus: any) => {
          if (
            subscriptionStatus.category === 'PUSHX' &&
            subscriptionStatus.type === 'CHANNEL_SUBSCRIPTION'
          ) {
            if (subscriptionStatus.status === 'SUCCESS') {
              associateClassProducts({
                classId: ltiClassId,
                productsCode: productsCode,
                spaceId: ltiSpaceKey
              }).then();
            } else if (subscriptionStatus.status === 'ERROR') {
              throw new Error(JSON.stringify(subscriptionStatus));
            }
          }
        }
      );
    });
  };

  const compareSessionsAndLtik = async (ltik: string, userData: any) => {
    userData = encodeURIComponent(
      btoa(encodeURIComponent(JSON.stringify(userData)))
    );
    return await $fetch(
      `/apigateway/compareSessionsAndLtik?ltik=${ltik}&userData=${userData}`
    );
  };

  const clearUserData = () => {
    const { userLoggedOut } = useLocalStorage();
    if (localStorage.getItem(userLoggedOut))
      localStorage.removeItem(userLoggedOut);
    localStorage.setItem(userLoggedOut, 'true');
    setDataFromUserAuthenticatedCall({});
    clearUserdataFromService();
  };

  const handleSessionsAndLtik = async (params: any, gigyaRes: any) => {
    if (!params.ltik) {
      return {
        success: false,
        routerRedirect: {
          path: '/generic-lti-error'
        }
      };
    } else {
      try {
        const gigyaUserData = {
          role: getC1UserRole(gigyaRes),
          ...gigyaRes.profile,
          extUserId: gigyaRes.UID
        };
        const response: any = await compareSessionsAndLtik(
          params.ltik,
          gigyaUserData
        );
        if (response.errorType) {
          switch (response.errorType) {
            case 'accountMismatch': {
              clearUserData();
              setLTIKData(params, response.ltiUser);
              return { success: true };
            }
            case 'toolUnauthorized':
            case 'ltikMismatch': {
              return {
                success: false,
                routerRedirect: {
                  path: '/generic-lti-error'
                }
              };
            }
            case 't_roleMismatch':
            case 's_roleMismatch':
            case 'p_t_roleMismatch':
            case 'p_s_roleMismatch': {
              window.gigya.accounts.logout();
              clearUserData();
              return {
                success: false,
                routerRedirect: {
                  path: `/lti-onboarding-error/${response.errorType}`
                }
              };
            }
            default:
              return {
                success: false,
                routerRedirect: {
                  path: `/lti-onboarding-error/${response.errorType}`
                }
              };
          }
        } else if (response.loggedInUser?.userAuthenticated) {
          setDataFromUserAuthenticatedCall(response.loggedInUser);
          sessionStorage.setItem(
            useRuntimeConfig().public.sessionStorage['userAccount'],
            JSON.stringify(response.loggedInUser.user)
          );
          setLTIKData(params, response.ltiUser);
          return { success: true };
        } else {
          setDataFromUserAuthenticatedCall(response.loggedInUser);
          setLTIKData(params, response.ltiUser);
          return { success: true };
        }
      } catch (error) {
        return {
          success: false,
          routerRedirect: {
            path: '/generic-lti-error'
          }
        };
      }
    }
  };

  const sendToActivateMaterial = (params: any) => {
    const {
      productFound: digitalComponent,
      classid,
      bundleCode,
      ltik,
      spaceKey,
      unit,
      activity,
      assignment,
      isProductExpired
    } = params;
    const options = {
      queryParams: {
        lf: 'ldl', // LTI deep link
        cid: classid, // classid,
        ltik: ltik, // lti token
        ...(unit && { unitid: unit }),
        ...(activity && { activityid: activity }),
        ...(assignment && { assignmentid: assignment }),
        ...(isProductExpired && { isexpired: 'true' })
      }
    };
    navigateToDashboardApp(
      [
        'teacher',
        spaceKey,
        'bundle',
        bundleCode,
        'digital-component',
        digitalComponent['dls-product'].code
      ],
      options
    );
  };

  const joinSpaceAndClass = async (shadowCheckData?: any) => {
    const user = await checkUserAuthenticatedCache();
    const ltikData: any = getLTIKData();
    cleanup();
    if (!shadowCheckData) {
      if (
        (user.role === 'teacher' || user.role === 'admin') &&
        ltikData?.features?.classBasedProgress === 'custom'
      ) {
        return await shadowProvisionOnlySpacePushX(user);
      }
    } else {
      useState('classData').value = shadowCheckData.classDetails;
    }

    if (['student', 'teacher', 'admin'].indexOf(user.role) > -1) {
      await shadowProvisionUserPushX(user);
    } else {
      navigateUserBasedOnRole(user.role);
    }
  };

  const shadowProvisionUserPushX = async (user: any) => {
    const ltikData: any = getLTIKData();
    await setupGrantByAccountIdPushX(
      {
        loggingInfo: {
          context: 'TYPE=SYSTEM_EVENTS, LTI flow, Shadow Provisioning'
        },
        onPushXCreate: async () => {
          const { success, response, status } = await shadowProvision({
            class: {
              classId: ltikData.extClassId,
              title: ltikData.classTitle,
              classLabel: ltikData.courseLabel
            },
            oid: ltikData.oid,
            orgId: ltikData.orgId,
            classBasedProgress: ltikData?.features?.classBasedProgress,
            isStudentEnrolLimitEnabled:
              ltikData?.features?.isStudentEnrolLimitEnabled,
            studentEnrolLimit: ltikData.studentEnrolLimit,
            sourceCourseIds: ltikData.sourceCourseIds,
            userData: useState('userData').value
          });
          if (!success) {
            if ((user.role === 'teacher' || user.role === 'admin') && status === 409) {
              // if teacher limit is reached in a LTI Class
              navigateTo({
                path: '/lti-onboarding-error/teacher_limit_reached'
              });
            } else if (user.role === 'student' && status === 409) {
              // if student limit is reached in a LTI Class
              navigateTo({
                path: '/lti-onboarding-error/learner_limit_reached'
              });
            }
            cleanup();
          } else {
            isShadowProvisionComplete = true;
            if (response.data['enrollment_exists']) {
              await onShadowProvisionSuccess(user.role, response.spaceKey);
            } else if (response.isClassCloning) {
              // In case of cloned class, check if class exists
              if (response.data['class_exists'] === false) {
                captureUserEnrollmentAdd = false;
              } else if (
                response.data['class_exists'] === true &&
                isEnrollmentEventReceived
              ) {
                captureUserEnrollmentAdd = true;
                if (!shadowProvisionSuccessComplete) {
                  await onShadowProvisionSuccess(user.role, response.spaceKey);
                }
              }
            } else if (isEnrollmentEventReceived) {
              await onShadowProvisionSuccess(user.role, response.spaceKey);
            }
          }
        }
      },
      async (eventContext: any, eventBody: any) => {
        const { ext_user_id, action, category, ext_classid, status } =
          eventBody;
        if (
          ext_user_id === user.extUserId &&
          action === 'USER_ENROLLMENT_ADD' &&
          category === 'CLASS' &&
          ext_classid === ltikData.extClassId
        ) {
          if (status === 'success') {
            isEnrollmentEventReceived = true;
            if (isShadowProvisionComplete && captureUserEnrollmentAdd) {
              await onShadowProvisionSuccess(user.role, eventBody.spacekey);
            }
          } else if (status === 'error') {
            errorHandling(user, eventContext);
          }
        }
        if (
          ext_user_id === user.extUserId &&
          action === 'CLASS_CLONING' &&
          category === 'SHADOW_PROVISIONING' &&
          ext_classid === ltikData.extClassId
        ) {
          if (status === 'success') {
            await onShadowProvisionSuccess(user.role, eventBody.spacekey);
          } else if (status === 'error') {
            errorHandling(user, eventContext);
          }
        }
      }
    );
  };

  const errorHandling = (user: any, eventContext: any) => {
    if (
      (user.role === 'teacher' || user.role === 'admin') &&
      eventContext?.events?.body?.statusCode === 409
    ) {
      // if teacher limit is reached in a LTI Class
      navigateTo({ path: '/lti-onboarding-error/teacher_limit_reached' });
    } else if (
      user.role === 'student' &&
      eventContext?.events?.body?.statusCode === 409
    ) {
      // if student limit is reached in a LTI Class
      navigateTo({ path: '/lti-onboarding-error/learner_limit_reached' });
    } else {
      throw new Error(JSON.stringify(eventContext));
    }
  };

  const onShadowProvisionSuccess = async (role: string, spaceId: string) => {
    if (shadowProvisionSuccessComplete) return;
    shadowProvisionSuccessComplete = true;
    let classData: any = useState('classData').value;
    const largeClassesList: any = loadLargeClassesList();
    const ltikData: any = getLTIKData();
    if (!classData) {
      // Get shadow Class Details in case class details not present
      const shadowClassData: any = await getParticularShadowClass(
        spaceId,
        ltikData.extClassId,
        { oid: ltikData.oid, orgId: ltikData.orgId, isProvisionNeeded: true }
      );
      classData = useState('classData').value = shadowClassData.classDetails;
    }
    const classDetails = classData;
    if (largeClassesList && largeClassesList.includes(classDetails.uuid)) {
      if (role == 'teacher' || role == 'admin') {
        navigateTo({ path: '/lti-onboarding-error/teacher_class_not_available' });
        return;
      } else if (role == 'student') {
        navigateTo({ path: '/lti-onboarding-error/learner_class_not_available' });
        return;
      }
    }
    const params = {
      spaceId,
      classId: classDetails.uuid,
      ltik: ltikData.ltik,
      resource: ltikData.resource
    };
    cleanup();
    const isProductInClass =
      classDetails?.products && classDetails.products.length > 0;
    if (role == 'student') {
      const resourceParts = decodeURIComponent(params.resource).split('/');
      if (isProductInClass) {
        if (
          ['unit', 'component', 'activity', 'assignment'].indexOf(
            resourceParts[0]
          ) > -1
        ) {
          const bundleCode = resourceParts[1];
          const isProductDifferent = await checkDifferentProductLink(
            classDetails,
            bundleCode
          );
          if (isProductDifferent) return;
          const products = classDetails.bundles[bundleCode].products;
          const productFound = products.find(
            (item: any) => item['dls-product'].code == resourceParts[2]
          );
          const isTeacherGatedComponent =
            productFound?.activation?.gated &&
            Object.hasOwn(productFound.activation.gated, 'teacher') &&
            !Object.hasOwn(productFound.activation.gated, 'student');
          /* Check if the component is teacher-gated and the type of the product is one of the following: 
          'OLP', 'TG', 'ASSET', 'PPLUS', 'EXT_RES', or 'imgbook' */
          if (
            isTeacherGatedComponent &&
            ['OLP', 'TG', 'ASSET', 'PPLUS', 'EXT_RES', 'imgbook'].indexOf(
              productFound['ext-product'].type
            ) !== -1
          ) {
            navigateTo({
              path: '/lti-onboarding-error/teacherResourceLink',
              query: { title: ltikData.resourceTitle }
            });
          } else {
            navigateTo({
              path: `/lti-activate-course-material/${params.spaceId}/class/${params.classId}/product/${ltikData.productCode}/resource/${encodeURIComponent(params.resource)}/ltik/${params.ltik}`
            });
          }
        } else {
          await navigateLTItoApp(
            spaceId,
            classDetails.uuid,
            { productCode: ltikData.productCode },
            role,
            ltikData.ltik,
            ltikData.resource
          );
        }
      } else {
        navigateTo({ path: '/lti-onboarding-error/course' });
      }
    } else if (role == 'teacher' || role == 'admin') {
      if (isProductInClass) {
        if (ltikData.resource === 'deeplink') {
          navigateTo({
            path:
              '/lti-teacher-dashboard/' +
              params.spaceId +
              '/class/' +
              params.classId +
              '/ltik/' +
              params.ltik
          });
        } else {
          await navigateLTItoApp(
            spaceId,
            classDetails.uuid,
            { productCode: ltikData.productCode },
            role,
            ltikData.ltik,
            ltikData.resource
          );
        }
      } else {
        navigateTo({
          path: `/lti-product-selection/${params.spaceId}/class/${params.classId}/resource/deeplink/ltik/${params.ltik}`
        });
      }
    }
  };

  const loadLargeClassesList = () => {
    const runtimeConfig = useRuntimeConfig();
    return runtimeConfig.public.largeClassesList || [];
  };

  const getUserRoleAndCountry = async (ltik: any) => {
    const userData = await $fetch('/apigateway/lti/getUserRoleAndCountry', {
      method: 'POST',
      body: { ltik: ltik }
    });
    return userData;
  };

  const shadowProvisionOnlySpacePushX = async (user: any) => {
    const ltikData: any = getLTIKData();
    let successCalled = false;
    setupGrantByAccountIdPushX(
      {
        loggingInfo: {
          context: 'TYPE=SYSTEM_EVENTS, LTI flow, Shadow Provisioning'
        },
        onPushXCreate: async () => {
          const { success, response } = await shadowProvisionOnlySpace({
            oid: ltikData.oid,
            orgId: ltikData.orgId,
            userData: useState('userData').value
          });
          if (!success) {
            cleanup();
          } else if (!successCalled) {
            successCalled = true;
            response.data['user_exists'] &&
              (await shadowProvisionOnlySpaceSuccess(user));
          }
        }
      },
      async (eventContext: any, eventBody: any) => {
        const { ext_user_id, action, category, status } = eventBody;
        if (
          ext_user_id === user.extUserId &&
          action === 'CREATE' &&
          category === 'USER'
        ) {
          if (status === 'success') {
            if (!successCalled) {
              successCalled = true;
              await shadowProvisionOnlySpaceSuccess(user);
            }
          } else if (status === 'error') {
            throw new Error(JSON.stringify(eventContext));
          }
        }
      }
    );
  };

  const shadowProvisionOnlySpaceSuccess = async (user: any) => {
    //Check if class already exists
    const ltikData: any = getLTIKData();
    const shadowCheckData: any = await checkParticularShadowClass(
      ltikData.extClassId,
      { oid: ltikData.oid, orgId: ltikData.orgId, isProvisionNeeded: true }
    );
    if (shadowCheckData.status == 'success') {
      useState('classData').value = shadowCheckData.classDetails;
      await shadowProvisionUserPushX(user);
    } else {
      const params = {
        spaceId: shadowCheckData.spaceId,
        extClassId: ltikData.extClassId,
        ltik: ltikData.ltik,
        resource: ltikData.resource
      };
      navigateTo({
        path:
          '/lti-progress-selection/' +
          params.spaceId +
          '/class/' +
          params.extClassId +
          '/resource/' +
          encodeURIComponent(params.resource) +
          '/ltik/' +
          params.ltik
      });
    }
  };

  const getIconData = (icon: string) => {
    switch (icon) {
      case 'nemo-font nemo-evolve2-tick':
        return {
          name: 'evolve2-tick',
          class: 'nemo-evolve2-tick d-inline-flex',
          shouldRender: true
        };
      case 'nemo-font nemo-teacher-resource':
        return {
          name: 'teacher-resource',
          class: 'nemo-teacher-resource d-inline-flex',
          shouldRender: true
        };
      case 'nemo-font nemo-audio-product':
        return {
          name: 'audio-product',
          class: 'nemo-audio-product d-inline-flex',
          shouldRender: true
        };
      case 'nemo-font nemo-test-generator':
        return {
          name: 'test-generator',
          class: 'nemo-test-generator d-inline-flex',
          shouldRender: true
        };
      case 'nemo-font nemo-presentation':
        return {
          name: 'presentation',
          class: 'nemo-presentation d-inline-flex',
          shouldRender: true
        };
      case 'nemo-font nemo-collaboration-plus':
        return {
          name: 'collaboration-plus',
          class: 'nemo-collaboration-plus d-inline-flex',
          shouldRender: true
        };
      case 'nemo-font nemo-mock-test':
        return {
          name: 'mock-test',
          class: 'nemo-mock-test d-inline-flex',
          shouldRender: true
        };
      case 'nemo-font nemo-external-resource':
        return {
          name: 'external-resource',
          class: 'nemo-external-resource d-inline-flex',
          shouldRender: true
        };
      // Add more cases as needed
      default:
        return {
          name: '',
          class: '',
          shouldRender: false
        };
    }
  };

  const setMaximumCourseLimit = () => {
    const runtimeConfig = useRuntimeConfig();
    const ltikData: any = useState('ltikData');
    const courseLimit = ltikData.value?.courseLimit;
    const isCourseLimitEnabled = ltikData.value?.features?.isCourseLimitEnabled;
    const defaultMaximumCourseLimit =
      runtimeConfig.public.defaultMaximumCourseLimit;
    //if 'isCourseLimitEnabled' is true and we get a valid value in 'courseLimit'
    if (isCourseLimitEnabled && courseLimit) {
      return parseInt(courseLimit, 10);
    } else {
      return defaultMaximumCourseLimit;
    }
  };

  const isTestTypeComponent = (bundleProduct: any) => {
    const resultsDisplayScoreDetailFormat = bundleProduct['results-display']?.['score-detail-format'] ? bundleProduct['results-display']['score-detail-format'] : scoreDetailFormatTypes.none;
    const productModelType = bundleProduct['ext-product']?.['model'];

    return resultsDisplayScoreDetailFormat === scoreDetailFormatTypes.mock || productModelType === productModelTypes.test;
  }
  const compinePplusEbook = (products: any) => {
    let pPlusEbooks: any = [];
    products.forEach((product: any) => {
      if (product["ext-product"].type === 'imgbook') {
        if (product.meta && (product.meta['cup-pplus-component'] || product.meta['pplus-component']) ) {
          pPlusEbooks.push(product);
        }
      }
    })
    return pPlusEbooks;
  }
  
  const isPplusEbookVisible = (digitalComponent: any, spaceId: string, checkForComingSoon = true) => {
    if (spaceId.indexOf("mqa") === 4) return true;
    if (digitalComponent.meta == undefined) return true;
    if (digitalComponent.meta.visibility == undefined) return true;
    if (digitalComponent.meta.visibility == 'true') return true;
    if (checkForComingSoon && digitalComponent.meta.visibility == 'coming-soon') return true;
    return false;
  }
  
  const getFirstVisibleEbook = (pPlusOnlyEbooks: any, spaceId: string) => {
    let firstVisibleEbook;
    if (pPlusOnlyEbooks.length > 0) {
      firstVisibleEbook = pPlusOnlyEbooks.find(ebook => isPplusEbookVisible(ebook, spaceId, false));
      if (!firstVisibleEbook) {
        firstVisibleEbook = pPlusOnlyEbooks.find(ebook => isPplusEbookVisible(ebook, spaceId, true));
      }
    }
    return firstVisibleEbook;
  }

  /* function to get leaf nodes of a node */
  const getLeafNodeItems = (node: any) => {
    let leafNodeItems: any = [];
    let leafNodeRecFun = function(rootNode: any) {
      if(rootNode['item-type'] !== 'folder') {
        leafNodeItems.push(rootNode);
      } else {
        let nodeItems = rootNode.items;
        let parentSeq = rootNode.sequence;
        nodeItems.forEach((nodeItem: any, nodeIndex: any) => {
            nodeItem.sequence = nodeIndex + 1;
            if(parentSeq) {
              nodeItem.sequence = `${parentSeq}.${nodeItem.sequence}`;
            }
            leafNodeRecFun(nodeItem);
        })
      }
    }
    leafNodeRecFun(node);
    return leafNodeItems;
  }

  return {
    gigyaUserdata,
    getGigiyaUserdata,
    getClassDetails,
    getAllBundleDetails,
    getfallbackLosCounts,
    addProductsToClass,
    isProductEntitledByUser,
    getSpaceAndClassDetails,
    getLTIKData,
    isMQASchool,
    checkParticularShadowClass,
    joinSpaceAndClass,
    shadowProvisionUserPushX,
    studentProductActivation,
    getMarkingQueueCount,
    handleSessionsAndLtik,
    ltiLogin,
    setLTIKData,
    getUserRoleAndCountry,
    getIconData,
    navigateLTItoApp,
    requestDeepLinkingForm,
    setMaximumCourseLimit,
    isTestTypeComponent,
    displayPopupBlockedMessage,
    compinePplusEbook,
    getFirstVisibleEbook,
    getOverlayJson,
    fetchProductsDetails,
    getLeafNodeItems
  };
}
